/**
 * Long-task lifecycle poller. Persistence routes through the React Query tasks
 * cache (`useTasksQuery`). Two poller branches: "mcp-task" tasks poll
 * /chat/tasks/{id}; "long-task" jobs poll /chat/long-jobs/status. Cross-tab
 * convergence comes from the manual focus refetch in the task-displaying routes,
 * not React Query focus invalidation, which the global `staleTime: Infinity`
 * makes a no-op (ADR 054). The terminal inject-once race is resolved by the
 * server's atomic `claim-terminal` endpoint.
 */
import React from "react";

import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { API } from "config";
import { useSetAtom } from "jotai";
import useClaimTerminalTaskMutation from "services/dbApi/hooks/useClaimTerminalTaskMutation";
import usePatchTaskMutation from "services/dbApi/hooks/usePatchTaskMutation";
import useTasksQuery, {
  TASKS_QUERY_KEY,
} from "services/dbApi/hooks/useTasksQuery";
import { buildMcpTaskTerminalInjection } from "types/longTask";
import type { Task } from "types/Task";
import type {
  TaskKind,
  TaskStatusValue,
  TerminalTaskStatus,
} from "types/TaskRegistry";
import {
  getTaskKind,
  isTaskStatusValue,
  shouldStopPolling,
  isKnownTerminalStatus,
} from "types/TaskRegistry";
import {
  applyFetchTaskTerminal,
  applyLongTaskTerminal,
} from "./longTaskTerminal";
import {
  LONG_TASK_NETWORK_ERROR_BUDGET,
  MAX_POLL_COUNT,
  clampPollInterval,
  FETCH_TRACKER_DEADLINE_MS,
  isFetchTrackerExpired,
  longTaskInterval,
} from "./taskPollConstants";
import {
  confirmTaskLive,
  exhaustTask,
  updateTaskStatus,
} from "./taskCacheWrites";
import {
  makeEnqueueInjection,
  pendingInjectionsAtom,
  type EnqueueInjection,
  type PendingInjectionsByChat,
} from "./usePendingInjections";
import {
  writeTaskToCache,
  type ClaimTerminal,
  type CommitTaskFn,
  type SetTaskFn,
} from "./useTaskCache";

// ---------------------------------------------------------------------------
// Utility
// ---------------------------------------------------------------------------

/** Removes map entries not in `keep`, running `cleanup` on each removed value. */
function pruneInactiveKeys<V>(
  map: Map<string, V>,
  keep: Set<string>,
  cleanup?: (value: V) => void
): void {
  for (const key of Array.from(map.keys())) {
    if (!keep.has(key)) {
      if (cleanup) cleanup(map.get(key) as V);
      map.delete(key);
    }
  }
}

// ---------------------------------------------------------------------------
// Poller branches
// ---------------------------------------------------------------------------

interface LongTaskStatusResponse {
  status: string;
  reason: string | null;
  raw_status: string;
  cached_at?: string | null;
}

interface PollDeps {
  /** Reads one row from the live cache. */
  getCachedTask: (jobId: string) => Task | undefined;
  getAccessToken: () => Promise<string>;
  setTask: SetTaskFn;
  commitTask: CommitTaskFn;
  schedule: (jobId: string, delayMs: number) => void;
  deleteTimer: (jobId: string) => void;
  /** Increments the consecutive-network-error count and returns the new value. */
  bumpNetworkErrors: (jobId: string) => number;
  /** Clears the network-error count after a successful status read. */
  resetNetworkErrors: (jobId: string) => void;
  /** Clears the poll-budget count after a successful non-terminal poll. */
  resetPollCount: (jobId: string) => void;
  /**
   * Marks the task poll-exhausted and drops its timer, ending polling for this
   * session. `reason` surfaces on the card; omit it to leave the existing one.
   */
  haltPolling: (jobId: string, reason?: string) => void;
  checkBudget: (
    jobId: string,
    max: number,
    onExhausted: () => Promise<void>
  ) => Promise<boolean>;
  onLongTaskTerminal: (
    jobId: string,
    status: TerminalTaskStatus,
    statusReason: string | null,
    statusRaw: string | null,
    cachedAt?: string | null
  ) => Promise<void>;
  onFetchTaskTerminal: (
    fetchTaskId: string,
    status: TerminalTaskStatus,
    result: unknown
  ) => Promise<void>;
  enqueue: EnqueueInjection;
}

/**
 * Settles a terminal MCP task. A fetch-tracker row routes through the
 * fetch-terminal handler, which owns committing the owning job's links and its
 * present-results directive; a plain MCP task commits its own status and
 * announces it unless dismissed.
 */
async function endMcpTask(
  job: Task,
  status: TerminalTaskStatus,
  result: unknown,
  deps: PollDeps
) {
  const { jobId } = job;
  if (job.fetchTaskId) {
    await deps.onFetchTaskTerminal(jobId, status, result);
  } else {
    deps.commitTask(jobId, { status });
    if (!job.dismissed) {
      deps.enqueue(
        job.threadId,
        buildMcpTaskTerminalInjection(jobId, status, job.threadId)
      );
    }
  }
  deps.deleteTimer(jobId);
}

async function pollMcpTask(job: Task, deps: PollDeps) {
  const { jobId } = job;
  let sawFailure = false;

  // Ends the task, keeping a claim/commit failure off the failure path below.
  // `applyFetchTaskTerminal` rethrows a transient claim failure, and charging that
  // to the poll budget would spend the task's allowance on an outage that has
  // nothing to do with reading status. It also puts the failure in DataDog, which
  // the surrounding catch would otherwise swallow. Mirrors `pollLongTask`'s
  // terminal wrapper.
  const endTask = async (status: TerminalTaskStatus, result: unknown) => {
    try {
      await endMcpTask(job, status, result, deps);
    } catch (error) {
      console.error(
        `Terminal handling for MCP task ${jobId} failed; rescheduling retry:`,
        error
      );
      const retry = deps.getCachedTask(jobId);
      if (retry && !shouldStopPolling(retry.status)) {
        deps.schedule(jobId, clampPollInterval(retry.pollInterval));
      } else {
        deps.deleteTimer(jobId);
      }
    }
  };

  try {
    const accessToken = await deps.getAccessToken();
    const res = await fetch(`${API.cortevaChain.url}/chat/tasks/${jobId}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (res.status === 404) {
      await endTask("failed", undefined);
      return;
    }

    if (res.ok) {
      const data = await res.json();
      const newStatus: TaskStatusValue = isTaskStatusValue(data?.status)
        ? data.status
        : "working";

      const backendName: unknown = data?.name;
      if (typeof backendName === "string" && backendName && !job.displayName) {
        deps.setTask(jobId, (prev) => ({ ...prev, displayName: backendName }));
      }

      if (isKnownTerminalStatus(newStatus)) {
        await endTask(newStatus, data?.result);
        return;
      }

      // A live source confirms the task is genuinely active; let the badge
      // count it (a reload-hydrated row stays uncounted until this point).
      confirmTaskLive({ jobId, setTask: deps.setTask });
      deps.resetPollCount(jobId);
    } else {
      sawFailure = true;
    }
  } catch {
    sawFailure = true;
  }

  // Decide whether to poll again. There are two ways a live task stops here:
  // 1) Fetch-trackers get a (wall clock) deadline rather than a failure budget.
  //    If it's past its deadline, it's marked as failed.
  // 2) An MCP task that's past its consecutive failure budget is marked as exhausted.
  const current = deps.getCachedTask(jobId);
  if (current && !shouldStopPolling(current.status)) {
    if (current.fetchTaskId) {
      if (isFetchTrackerExpired(current.createdAt)) {
        // Error level so Datadog collects it: a fetch that never finishes leaves
        // the owning job's results unharvested until the user clicks "Retry
        // fetch", and a systematic upstream fetch failure is otherwise silent.
        console.error(
          `Fetch tracker ${jobId} for job ${current.ownerJobId} outlived its ` +
            `${FETCH_TRACKER_DEADLINE_MS}ms deadline (created ${current.createdAt}); ` +
            `marking it failed. No results were recorded.`
        );
        // The terminal handler PATCHes the tracker dismissed; drop the timer even
        // if that write throws, since the tracker will not be polled again and a
        // leaked timer entry leaves stale scheduling state until the next prune.
        try {
          await deps.onFetchTaskTerminal(jobId, "failed", undefined);
        } finally {
          deps.deleteTimer(jobId);
        }
        return;
      }
    } else if (sawFailure) {
      const exhausted = await deps.checkBudget(
        jobId,
        MAX_POLL_COUNT,
        async () => exhaustTask({ jobId, setTask: deps.setTask })
      );
      if (exhausted) return;
    }

    deps.schedule(jobId, clampPollInterval(current.pollInterval));
  }
}

async function pollLongTask(job: Task, deps: PollDeps) {
  const { jobId } = job;
  // `statusTool`/`serverUrl` come from the row, captured at submit, so polling
  // works regardless of which persona is loaded now
  const statusTool = job.statusTool ?? "";

  let sawNetworkError = false;
  try {
    const accessToken = await deps.getAccessToken();
    const params = new URLSearchParams({
      server: job.serverUrl ?? "",
      group: job.groupName ?? "",
      jobId,
      statusTool,
    });
    const res = await fetch(
      `${API.cortevaChain.url}/chat/long-jobs/status?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "X-MCP-Oauth-Scopes": job.oauthScopes ?? "",
          // The chain proxy forwards this to the MCP server, whose status tool
          // may scope the job to the chat thread that submitted it (btsat's
          // rejects the call outright without it). Needed on every poll, not
          // only on the jobId-less thread-resolved variant.
          "X-Chat-Thread-ID": job.threadId,
        },
      }
    );

    if (res.status === 401 || res.status === 403) {
      sawNetworkError = true;
    } else if (res.status >= 400 && res.status < 500) {
      console.error(
        `Long-task status poll for ${jobId} returned ${res.status}; ` +
          `treating as a non-transient config error and halting polling`
      );
      deps.haltPolling(
        jobId,
        `Status check failed (HTTP ${res.status}) — likely a configuration error, not a transient failure. Polling halted; use Check Status to retry.`
      );
      return;
    } else if (!res.ok) {
      sawNetworkError = true;
    } else {
      const data: LongTaskStatusResponse = await res.json();
      const status = data.status;
      const cachedAt = data.cached_at ?? null;

      if (isKnownTerminalStatus(status)) {
        try {
          await deps.onLongTaskTerminal(
            jobId,
            status,
            data.reason,
            data.raw_status,
            cachedAt
          );
        } catch (error) {
          // The status fetch succeeded; this is a claim/commit failure (e.g. a
          // transient 5xx on claim-terminal). applyLongTaskTerminal leaves the
          // row non-terminal on a thrown claim, so reschedule an in-session
          // retry. Deliberately NOT the network-error path: a claim failure is
          // not a status-fetch network failure and must not burn that budget.
          console.error(
            `Terminal handling for job ${jobId} failed; rescheduling retry:`,
            error
          );
          const retry = deps.getCachedTask(jobId);
          if (retry && !shouldStopPolling(retry.status)) {
            deps.schedule(jobId, longTaskInterval(retry.createdAt));
          } else {
            deps.deleteTimer(jobId);
          }
          return;
        }
        deps.deleteTimer(jobId);
        return;
      }

      updateTaskStatus({
        jobId,
        status,
        statusReason: data.reason,
        statusRaw: data.raw_status,
        cachedAt,
        setTask: deps.setTask,
      });
      deps.resetNetworkErrors(jobId);
    }
  } catch {
    sawNetworkError = true;
  }

  const current = deps.getCachedTask(jobId);
  if (!current || shouldStopPolling(current.status)) {
    deps.deleteTimer(jobId);
    return;
  }

  if (sawNetworkError) {
    if (deps.bumpNetworkErrors(jobId) >= LONG_TASK_NETWORK_ERROR_BUDGET) {
      deps.haltPolling(jobId);
      return;
    }
  }

  deps.schedule(jobId, longTaskInterval(current.createdAt));
}

// ---------------------------------------------------------------------------
// Shared task-cache ops
// ---------------------------------------------------------------------------

/**
 * The cache-mutation closures the live sweep and the poller both need. Shared
 * so a fix to the commit/claim handling lands in one place. Both call sites (the
 * poller and the sweep) pass the same live query-cache reader; `getTasks` is a
 * parameter only because this is a plain function and cannot call
 * `useQueryClient` itself.
 *
 * `commitTask` resolves the row by `jobId` only (every caller passes a jobId,
 * never a PK), and `setTask` leans on React Query's structural sharing to keep the
 * array reference on a tick that changed nothing, so cache consumers don't
 * re-render (see `writeTaskToCache` for the one case that does not collapse).
 */
interface TaskCacheOps {
  getTask: (jobId: Task["jobId"]) => Task | undefined;
  setTask: SetTaskFn;
  commitTask: CommitTaskFn;
  claimTerminal: ClaimTerminal;
  enqueueInjection: EnqueueInjection;
}

interface MakeTaskCacheOpsArgs {
  getTasks: () => Task[];
  queryClient: QueryClient;
  /** React Query binds `mutate`/`mutateAsync` once per observer, so callers pass
   *  the members directly rather than a ref to the whole result object. */
  patchTask: ReturnType<typeof usePatchTaskMutation>["mutate"];
  claimTask: ReturnType<typeof useClaimTerminalTaskMutation>["mutateAsync"];
  setPendingInjections: (
    updater: (prev: PendingInjectionsByChat) => PendingInjectionsByChat
  ) => void;
}

export function makeTaskCacheOps({
  getTasks,
  queryClient,
  patchTask,
  claimTask,
  setPendingInjections,
}: MakeTaskCacheOpsArgs): TaskCacheOps {
  const setTask: SetTaskFn = (jobId, updater) =>
    writeTaskToCache(queryClient, jobId, updater);

  const getTask = (jobId: string): Task | undefined =>
    getTasks().find((j) => j.jobId === jobId);

  const commitTask: CommitTaskFn = (jobId, fields) => {
    // Resolve the row by jobId (the module-wide key), then address it by its
    // PK for the cache write and PATCH.
    const task = getTask(jobId);
    if (!task) return;
    queryClient.setQueryData<Task[]>(TASKS_QUERY_KEY, (prev) =>
      prev?.map((j) => (j.id === task.id ? { ...j, ...fields } : j))
    );
    patchTask({ id: task.id, patch: fields });
  };

  const claimTerminal: ClaimTerminal = async (jobId) => {
    // The claim endpoint addresses the row by its PK; resolve it from the
    // jobId the callers hold.
    const task = getTask(jobId);
    if (!task) return { claimed: false, gone: true };
    const res = await claimTask(task.id);
    return { claimed: res.claimed, gone: res.gone };
  };

  // Functional update so concurrent enqueues (peer tabs, sweep) don't clobber
  // each other or other chats' queues.
  const enqueueInjection = makeEnqueueInjection(setPendingInjections);

  return { getTask, setTask, commitTask, claimTerminal, enqueueInjection };
}

/**
 * Polls all non-terminal, non-exhausted jobs. Call once at the app level
 * (`App.tsx`), not from a route or a route-scoped component: the timer and
 * budget refs below are mount-scoped, so a host that remounts on navigation
 * resets every budget and force-polls every active job (ADR 059).
 */
export function useTaskPolling(getAccessToken: () => Promise<string>): void {
  const queryClient = useQueryClient();
  const tasksQuery = useTasksQuery();
  const setPendingInjections = useSetAtom(pendingInjectionsAtom);
  // Destructured because React Query binds these once per observer; the result
  // object itself is recreated each render.
  const { mutate: patchTask } = usePatchTaskMutation();
  const { mutateAsync: claimTask } = useClaimTerminalTaskMutation();

  const timersRef = React.useRef(
    new Map<string, ReturnType<typeof setTimeout>>()
  );
  const pollCountsRef = React.useRef(new Map<string, number>());
  const networkErrorCountsRef = React.useRef(new Map<string, number>());

  React.useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const handle of timers.values()) {
        clearTimeout(handle);
      }
      timers.clear();
    };
  }, []);

  // Stable string of active job IDs; the effect deps only change when the set changes.
  const activeTaskIds = React.useMemo(() => {
    return (tasksQuery.data ?? [])
      .filter((j) => !shouldStopPolling(j.status) && !j.pollExhausted)
      .map((j) => j.jobId)
      .sort()
      .join(",");
  }, [tasksQuery.data]);

  React.useEffect(() => {
    const timers = timersRef.current;
    const pollCounts = pollCountsRef.current;
    const networkErrorCounts = networkErrorCountsRef.current;

    const ids = activeTaskIds ? activeTaskIds.split(",") : [];
    const idSet = new Set(ids);

    pruneInactiveKeys(timers, idSet, (handle) => clearTimeout(handle));
    pruneInactiveKeys(pollCounts, idSet);
    pruneInactiveKeys(networkErrorCounts, idSet);

    // Reads the live query cache, the same reader the sweep passes: these ops run
    // after the pollers' `await`s, so a snapshot taken at render would be stale.
    const { getTask, setTask, commitTask, claimTerminal, enqueueInjection } =
      makeTaskCacheOps({
        getTasks: () => queryClient.getQueryData<Task[]>(TASKS_QUERY_KEY) ?? [],
        queryClient,
        patchTask,
        claimTask,
        setPendingInjections,
      });

    async function consumePollBudget(
      jobId: string,
      max: number,
      onExhausted: () => Promise<void>
    ): Promise<boolean> {
      const count = (pollCounts.get(jobId) ?? 0) + 1;
      pollCounts.set(jobId, count);
      if (count >= max) {
        // Delete the timer even if onExhausted throws: a leaked timer entry
        // leaves stale scheduling state until the next prune.
        try {
          await onExhausted();
        } finally {
          timers.delete(jobId);
        }
        return true;
      }
      return false;
    }

    function scheduleTask(jobId: string, delayMs: number) {
      const existing = timers.get(jobId);
      if (existing) clearTimeout(existing);
      const handle = setTimeout(() => {
        void pollTask(jobId).catch((err) =>
          console.error(`Poll for task ${jobId} threw:`, err)
        );
      }, delayMs);
      timers.set(jobId, handle);
    }

    const pollDeps: PollDeps = {
      // Reads the live cache, so the post-fetch reschedule check sees a task
      // evicted mid-poll (e.g. chat deletion) at once instead of firing one
      // redundant reschedule.
      getCachedTask: getTask,
      getAccessToken,
      setTask,
      commitTask,
      schedule: scheduleTask,
      deleteTimer: (jobId) => timers.delete(jobId),
      bumpNetworkErrors: (jobId) => {
        const count = (networkErrorCounts.get(jobId) ?? 0) + 1;
        networkErrorCounts.set(jobId, count);
        return count;
      },
      resetNetworkErrors: (jobId) => networkErrorCounts.set(jobId, 0),
      resetPollCount: (jobId) => pollCounts.set(jobId, 0),
      haltPolling: (jobId, reason) => {
        exhaustTask({ jobId, setTask, reason });
        timers.delete(jobId);
      },
      checkBudget: consumePollBudget,
      onLongTaskTerminal: handleLongTaskTerminal,
      onFetchTaskTerminal: handleFetchTaskTerminal,
      enqueue: enqueueInjection,
    };

    async function pollTask(jobId: string) {
      // Read the live cache (same source as pollDeps.getCachedTask) so a task
      // evicted between scheduling and firing (e.g. chat deletion) is seen at
      // once, instead of acting on a stale snapshot.
      const job = pollDeps.getCachedTask(jobId);

      if (!job || shouldStopPolling(job.status)) return;
      const kind: TaskKind = getTaskKind(job);
      if (kind === "long-task") {
        await pollLongTask(job, pollDeps);
      } else {
        await pollMcpTask(job, pollDeps);
      }
    }

    async function handleLongTaskTerminal(
      jobId: string,
      status: TerminalTaskStatus,
      statusReason: string | null,
      statusRaw: string | null,
      cachedAt?: string | null
    ) {
      const job = getTask(jobId);
      if (!job) return;

      await applyLongTaskTerminal(jobId, status, {
        statusReason,
        statusRaw,
        cachedAt,
        deps: {
          getTask,
          commitTask,
          claimTerminal,
          enqueue: enqueueInjection,
        },
      });
    }

    async function handleFetchTaskTerminal(
      fetchTaskId: string,
      status: TerminalTaskStatus,
      result: unknown
    ) {
      await applyFetchTaskTerminal(fetchTaskId, status, {
        result,
        deps: {
          getTask,
          commitTask,
          claimTerminal,
          enqueue: enqueueInjection,
        },
      });
    }

    for (const jobId of ids) {
      if (!timers.has(jobId)) {
        scheduleTask(jobId, 0);
      }
    }
    // `patchTask`/`claimTask` are React Query's per-observer bound members, so
    // listing them does not re-run the poller on every render.
  }, [
    activeTaskIds,
    getAccessToken,
    queryClient,
    setPendingInjections,
    patchTask,
    claimTask,
  ]);
}
